import { FC, forwardRef, useCallback, useRef, useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { StaticTimePicker, StaticTimePickerProps } from '@mui/x-date-pickers';
import { format as dateFormatter } from 'date-fns';
import { CoreFormControl, CoreFormControlProps } from '../core-form-control';
import { coreTimePickerStyles } from './core-time-picker.styles';
import {
	Colors,
	inputPlaceholderColor,
	inputForegroundColor,
	inputDisabledForegroundColor,
	inputBackgroundColor,
	inputBorderColor,
	inputDisabledBorderColor,
	inputFocusBorderColor,
	inputErrorBorderColor,
} from '../../../constants';
import { CalendarIcon as Calendar } from '../../../icons';

export interface CoreTimePickerProps
	extends CoreFormControlProps,
		Omit<StaticTimePickerProps<Date | string | number>, 'onChange'> {
	format?: string;
	CalendarIcon?: FC;
	hideIcons?: boolean;
	placeholder?: string;
	onChange?(value: Date): void;
}

export const CoreTimePicker: FC<CoreTimePickerProps> = forwardRef((props, ref) => {
	const {
		id,
		disabled,
		error,
		helperText,
		label,
		requiredMark,
		focused,
		fullWidth,
		controlSx,
		labelSx,
		value,
		format = 'EEE MMM dd @h:mmaaa',
		onChange,
		CalendarIcon = Calendar,
		hideIcons,
		placeholder = '',
		...timePickerProps
	} = props;
	const [open, setMenuVisibility] = useState(false);
	const anchorRef = useRef(null);

	const date = typeof value === 'string' && value ? new Date(value) : value instanceof Date ? value : undefined;

	const handleClose = useCallback((): void => {
		setMenuVisibility(false);
	}, []);

	const handleOpen = useCallback(() => {
		if (disabled) {
			return;
		}

		setMenuVisibility(true);
	}, [disabled]);

	return (
		<CoreFormControl
			error={error}
			helperText={helperText}
			label={label}
			id={id}
			requiredMark={requiredMark}
			focused={focused}
			fullWidth={fullWidth}
			controlSx={controlSx}
			labelSx={labelSx}
		>
			<Box
				ref={anchorRef}
				onClick={handleOpen}
				sx={{
					width: '100%',
					height: '40px',
					borderRadius: '8px',
					p: '10px 16px',
					borderWidth: '1px',
					borderStyle: 'solid',
					cursor: disabled ? 'default' : 'pointer',
					display: 'flex',
					alignItems: 'center',
					backgroundColor: inputBackgroundColor,
					color: disabled ? inputDisabledForegroundColor : inputForegroundColor,
					borderColor: disabled
						? inputDisabledBorderColor
						: error
						? inputErrorBorderColor
						: open
						? inputFocusBorderColor
						: inputBorderColor,

					'& > svg': {
						display: hideIcons ? 'none' : 'block',
						height: '18px',
						width: '18px',
						mr: '10px',
					},

					'&:hover, &:focus, &:active': {
						borderColor: disabled ? inputDisabledBorderColor : error ? inputErrorBorderColor : inputFocusBorderColor,
					},
				}}
			>
				<Box component={CalendarIcon} />
				<Typography
					sx={{
						fontFamily: 'Montserrat, sans-serif',
						fontWeight: 500,
						fontSize: '16px',
						lineHeight: '19px',
						color: disabled ? inputDisabledForegroundColor : date ? inputForegroundColor : inputPlaceholderColor,
					}}
				>
					{date ? dateFormatter(date, format) : placeholder}
				</Typography>
			</Box>
			<Popover
				open={open}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				PaperProps={{
					sx: {
						borderRadius: '4px',
						boxShadow: `0px 2px 4px 0px ${Colors.black_a20}`,
						marginTop: '8px',
						backgroundColor: Colors.transparent,
					},
				}}
			>
				<StaticTimePicker
					{...timePickerProps}
					value={date}
					disabled={disabled}
					slotProps={{
						actionBar: { actions: [] },
					}}
					sx={coreTimePickerStyles}
					onChange={value => onChange?.(value as Date)}
				/>
			</Popover>
		</CoreFormControl>
	);
});

CoreTimePicker.displayName = 'CoreTimePicker';
